import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { CircleMarker, Marker, Polygon, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import {
  UNIT_LABEL, UNIT_NAME, distanceM, bearingDeg, formatBearing, formatDistance, formatLatLng,
  pathDistanceM, routePath, wrapPoint, circleRing,
} from "../helpers/geo";
import { parseCoords } from "../helpers/coordinates";

/* ================================================================
   Pieces that live INSIDE <MapContainer>
   ================================================================ */

// Hands the Leaflet map to the app so tools (fit, fullscreen…) can drive it.
export function MapBridge({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; return () => { mapRef.current = null; }; }, [map, mapRef]);

  // Leaflet only notices window resizes. The map's box also changes when the mobile notice is dismissed,
  // the phone rotates or the browser's address bar slides away, so re-measure whenever the container resizes.
  useEffect(() => {
    const el = map.getContainer();
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => map.invalidateSize({ pan: false }));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [map]);

  return null;
}

const labelIcon = (html, cls = "", anchor = [0, 0]) =>
  L.divIcon({ className: `measure-label ${cls}`, html: `<span>${html}</span>`, iconSize: [0, 0], iconAnchor: anchor });

const vertexIcon = (n, first, last) =>
  L.divIcon({
    className: `measure-vertex${first ? " first" : ""}${last ? " last" : ""}`,
    html: `<span>${n}</span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

// The measured route: curved great-circle legs, draggable points, a live "rubber band" to the cursor, and labels.
export function MeasureLayer({ points, active, unit, onAdd, onMove, onRemove }) {
  const [cursor, setCursor] = useState(null);

  useMapEvents({
    click: (e) => active && onAdd([e.latlng.lat, e.latlng.lng]),
    mousemove: (e) => active && points.length > 0 && setCursor([e.latlng.lat, e.latlng.lng]),
    mouseout: () => setCursor(null),
  });
  useEffect(() => { if (!active) setCursor(null); }, [active]);

  const { path, mids } = useMemo(() => routePath(points), [points]);
  const legs = useMemo(() => points.slice(1).map((p, i) => distanceM(points[i], p)), [points]);
  const total = useMemo(() => legs.reduce((a, b) => a + b, 0), [legs]);

  // live preview from the last point to the pointer
  const last = points[points.length - 1];
  const live = active && last && cursor ? routePath([last, cursor]) : null;
  const liveLeg = active && last && cursor ? distanceM(last, cursor) : 0;

  if (!points.length) return null;
  return (
    <>
      {path.length > 1 && <Polyline positions={path} pathOptions={{ color: "#e0452f", weight: 3, opacity: 0.95 }} />}
      {live && <Polyline positions={live.path} pathOptions={{ color: "#e0452f", weight: 2, dashArray: "6 6", opacity: 0.7 }} interactive={false} />}

      {legs.length <= 25 && mids.map((m, i) => (
        <Marker key={`l${i}`} position={m} interactive={false} keyboard={false} icon={labelIcon(formatDistance(legs[i], unit))} />
      ))}

      {points.map((p, i) => (
        <Marker
          key={i}
          position={p}
          draggable
          icon={vertexIcon(i + 1, i === 0, i === points.length - 1)}
          zIndexOffset={1000}
          eventHandlers={{
            dragend: (e) => { const ll = e.target.getLatLng(); onMove(i, [ll.lat, ll.lng]); },
            contextmenu: (e) => { L.DomEvent.stop(e); onRemove(i); },
          }}
        >
          <Tooltip direction="top" offset={[0, -10]}>
            Point {i + 1} · {formatLatLng(p[0], p[1])}<br />
            <small>Drag to move · right-click or long-press to remove</small>
          </Tooltip>
        </Marker>
      ))}

      {points.length > 1 && !live && (
        <Marker position={last} interactive={false} keyboard={false} icon={labelIcon(`Total ${formatDistance(total, unit)}`, "measure-total", [-14, 8])} />
      )}
      {live && (
        <Marker
          position={cursor}
          interactive={false}
          keyboard={false}
          icon={labelIcon(`${formatDistance(liveLeg, unit)}${points.length > 1 ? ` · total ${formatDistance(total + liveLeg, unit)}` : ""}`, "measure-live", [-16, 26])}
        />
      )}
    </>
  );
}

// Writes the cursor position straight into a DOM node (no React re-render on every mouse move) and drops pins on click.
export function CoordinatesTracker({ active, track, format, outRef, onPin }) {
  const raf = useRef(0);
  useMapEvents({
    mousemove: (e) => {
      if (!track) return;
      const { lat, lng } = e.latlng;
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => { if (outRef.current) outRef.current.textContent = formatLatLng(lat, lng, format); });
    },
    mouseout: () => { if (outRef.current) outRef.current.textContent = "—"; },
    click: (e) => active && onPin(wrapPoint([e.latlng.lat, e.latlng.lng])),
  });
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return null;
}

export function PinMarker({ pin, format }) {
  if (!pin) return null;
  return (
    <CircleMarker center={pin} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#e0452f", fillOpacity: 1 }}>
      <Tooltip permanent direction="top" offset={[0, -8]} className="pin-tooltip">{formatLatLng(pin[0], pin[1], format)}</Tooltip>
    </CircleMarker>
  );
}

export function RadiusLayer({ active, center, radiusM, onPick }) {
  useMapEvents({ click: (e) => active && onPick([e.latlng.lat, e.latlng.lng]) });
  const ring = useMemo(() => (center && radiusM ? circleRing(center, radiusM) : null), [center, radiusM]);
  if (!center) return null;
  return (
    <>
      {ring && <Polygon positions={ring} pathOptions={{ color: "#1f8fa3", weight: 2, fillColor: "#1f8fa3", fillOpacity: 0.1 }} interactive={false} />}
      <CircleMarker center={center} radius={5} pathOptions={{ color: "#fff", weight: 2, fillColor: "#1f8fa3", fillOpacity: 1 }} interactive={false} />
    </>
  );
}

/* ================================================================
   Overlays that sit on top of the map (render beside <MapContainer>)
   ================================================================ */

const UNITS = ["km", "nm", "mi"];
function UnitSwitch({ unit, onChange }) {
  return (
    <div className="unit-switch" role="group" aria-label="Distance unit">
      {UNITS.map((u) => (
        <button key={u} type="button" className={unit === u ? "on" : ""} aria-pressed={unit === u} title={UNIT_NAME[u]} onClick={() => onChange(u)}>{UNIT_LABEL[u]}</button>
      ))}
    </div>
  );
}

export function MeasurePanel({ active, points, unit, onUnit, onUndo, onClear, onCopy, onDone, onResume }) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape" && !document.querySelector(".slide-panel")) onDone(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onDone]);

  const legs = useMemo(() => points.slice(1).map((p, i) => ({ d: distanceM(points[i], p), b: bearingDeg(points[i], p) })), [points]);
  const total = useMemo(() => pathDistanceM(points), [points]);
  if (!active && points.length === 0) return null;

  return (
    <div className="map-tool-panel measure-panel" role="region" aria-label="Distance measurement">
      <div className="mtp-head">
        <strong>📏 Measure distance</strong>
        <UnitSwitch unit={unit} onChange={onUnit} />
      </div>

      {points.length < 2 ? (
        <p className="mtp-hint">
          {active
            ? points.length === 0 ? "Click or tap the map or a wreck to place the first point." : "Click or tap again to place the next point."
            : "Not enough points to measure."}
        </p>
      ) : (
        <>
          <div className="mtp-total">{formatDistance(total, unit)}</div>
          <p className="mtp-sub">{points.length - 1} leg{points.length === 2 ? "" : "s"} · shortest path over the WGS-84 ellipsoid</p>
          <ol className="mtp-legs">
            {legs.map((l, i) => (
              <li key={i}><span>{i + 1}→{i + 2}</span><b>{formatDistance(l.d, unit)}</b><em>{formatBearing(l.b)}</em></li>
            ))}
          </ol>
        </>
      )}
      {active && points.length > 0 && <p className="mtp-hint small">Drag a point to adjust it · right-click or long-press a point to remove it · press Done to finish</p>}

      <div className="mtp-actions">
        <button type="button" onClick={onUndo} disabled={!points.length}>↶ Undo</button>
        <button type="button" onClick={onCopy} disabled={points.length < 2}>⧉ Copy</button>
        <button type="button" onClick={onClear} disabled={!points.length}>✕ Clear</button>
        {active ? <button type="button" className="primary" onClick={onDone}>Done</button> : <button type="button" className="primary" onClick={onResume}>＋ Add points</button>}
      </div>
    </div>
  );
}

export function CoordinatesBar({ visible, outRef, format, onFormat, pin, onClearPin, onCopy }) {
  if (!visible) return null;
  return (
    <div className="map-tool-panel coords-bar" role="region" aria-label="Coordinates">
      <div className="coords-line">
        <span className="coords-tag">{pin ? "Pinned" : "Cursor"}</span>
        <span className="coords-value" ref={outRef}>{pin ? formatLatLng(pin[0], pin[1], format) : "—"}</span>
      </div>
      <div className="coords-actions">
        <button type="button" onClick={() => onFormat(format === "dd" ? "dms" : "dd")} title="Switch between decimal degrees and degrees-minutes-seconds">{format === "dd" ? "DD" : "DMS"}</button>
        <button type="button" onClick={onCopy} title="Copy coordinates">⧉</button>
        {pin && <button type="button" onClick={onClearPin} title="Remove pin" aria-label="Remove pin">✕</button>}
      </div>
      {!pin && <small className="coords-hint">Click or tap the map to drop a pin</small>}
    </div>
  );
}

export function GoToBox({ onGo, onClose }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const c = parseCoords(text);
    if (!c) return setError("Couldn't read that. Try “41.7325, -49.9469” or 41°43′57″N 49°56′48″W");
    setError("");
    onGo(c.lat, c.lng);
  };
  return (
    <form className="map-tool-panel goto-box" onSubmit={submit}>
      <div className="mtp-head">
        <strong>🎯 Go to coordinates</strong>
        <button type="button" className="mtp-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="goto-row">
        <input autoFocus type="text" placeholder="Paste coordinates…" value={text} onChange={(e) => { setText(e.target.value); setError(""); }} className={error ? "invalid" : ""} aria-label="Coordinates" />
        <button type="submit" className="primary" disabled={!text.trim()}>Go</button>
      </div>
      {error ? <small className="bad">{error}</small> : <small>Decimal degrees or degrees-minutes-seconds, with N/S/E/W if you like.</small>}
    </form>
  );
}

export function RadiusPanel({
  center, unit, onUnit, value, onValue, radiusM, tooBig, results,
  onMyLocation, onFit, onSelectShip, onClose,
}) {
  return (
    <div className="map-tool-panel radius-panel" role="region" aria-label="Wrecks within radius">
      <div className="mtp-head">
        <strong>◎ Wrecks within radius</strong>
        <button type="button" className="mtp-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="radius-controls">
        <label>
          Radius
          <input type="number" min="0" step="any" inputMode="decimal" value={value} onChange={(e) => onValue(e.target.value)} />
        </label>
        <UnitSwitch unit={unit} onChange={onUnit} />
      </div>
      {tooBig && <small className="bad">Capped at 5,000 km.</small>}
      <div className="mtp-actions">
        <button type="button" onClick={onMyLocation}>◎ My location</button>
        <button type="button" onClick={onFit} disabled={!center || !radiusM}>⤢ Zoom to circle</button>
      </div>

      {!center ? (
        <p className="mtp-hint">Click the map to set the centre of the search.</p>
      ) : (
        <>
          <p className="mtp-sub">
            <b>{results.length}</b> wreck{results.length === 1 ? "" : "s"} within {formatDistance(radiusM, unit)} of {formatLatLng(center[0], center[1])}
          </p>
          <ol className="radius-results">
            {results.slice(0, 50).map(({ ship, d, bearing }) => (
              <li key={ship.id}>
                <button type="button" onClick={() => onSelectShip(ship)}>
                  <span className="rr-name">{ship.title}</span>
                  <span className="rr-meta">{formatDistance(d, unit)} · {formatBearing(bearing)}</span>
                </button>
              </li>
            ))}
          </ol>
          {results.length > 50 && <small>Showing the nearest 50.</small>}
        </>
      )}
    </div>
  );
}
